import React, { useEffect, useRef, useState } from 'react';
import { ScrollView, Text, TouchableOpacity, View } from 'react-native';
import { ModalAwareScrollView } from '../../shared/modals/ModalAwareScrollView';
import { spacing } from '../../shared/theme/spacing';
import { useHasBusinessCapability } from '../../shell/session/hooks/useEffectiveBusinessCapabilities';
import { usePublicStoreFeaturesVisible } from '../../shell/feature-flags/usePublicStoreFeaturesVisible';
import { CatalogProductFormCodesFields } from './CatalogProductFormCodesFields';
import { CatalogProductFormDraftValidation } from './CatalogProductFormDraftValidation';
import { CatalogProductFormIdentityFields } from './CatalogProductFormIdentityFields';
import { CatalogProductFormNotesFields, CatalogProductFormOrganizationFields } from './CatalogProductFormOrganizationFields';
import { CatalogProductFormPublicStoreFields } from './CatalogProductFormPublicStoreFields';
import { CatalogProductFormPricingFields } from './CatalogProductFormPricingFields';
import { CatalogProductFormBookingFields } from './CatalogProductFormBookingFields';
import { CatalogProductFormKitchenDestinationFields } from './CatalogProductFormKitchenDestinationFields';
import { CatalogProductFormPharmacyFields } from './CatalogProductFormPharmacyFields';
import { CatalogProductFormMenuFilterFields } from './CatalogProductFormMenuFilterFields';
import { CatalogProductFormVariantsFields } from './CatalogProductFormVariantsFields';
import { catalogProductFormStyles as styles } from './catalogProductForm.styles';
import type { CatalogProductFormFieldsProps } from './catalogProductFormFields.types';
import { useModulePermission } from '../../shell/session/hooks/useModulePermission';
import { CatalogProductFormCompositionFields } from './CatalogProductFormCompositionFields';
import { CatalogProductRecipePanel } from './CatalogProductRecipePanel';
import { useCatalogOverlayStore } from '../store/useCatalogOverlayStore';
import type { RecipeKind } from '../infra/recipesRemoteRepo';

export type { CatalogProductFormFieldsProps } from './catalogProductFormFields.types';

type FormSectionDef = {
  key: string;
  /** Label del rail desktop. */
  label: string;
  glyph: string;
  optionalLabel?: string;
  node: React.ReactNode;
};

export function CatalogProductFormFields(props: CatalogProductFormFieldsProps) {
  const { mobile = false, formCompact = false, formMode = 'create', onExpandForm, barcodeInputRef } = props;
  const serviceCatalogEnabled = useHasBusinessCapability('service_catalog');
  const purchasesEnabled = useHasBusinessCapability('purchases');
  const menuQrEnabled = useHasBusinessCapability('tables');
  const posEnabled = useHasBusinessCapability('pos');
  const onlineStoreEnabled = usePublicStoreFeaturesVisible();
  const showBooking =
    serviceCatalogEnabled && props.draft.itemType === 'service';
  const availabilityEnabled =
    posEnabled || onlineStoreEnabled || (menuQrEnabled && props.draft.itemType === 'physical');
  const showCompact = formCompact && formMode === 'create';
  // Rail + panel exclusivo solo en desktop de form completo.
  const desktopQuickMode = !mobile && showCompact;
  const railMode = !mobile && !showCompact;

  const [activeSection, setActiveSection] = useState('identity');
  const [bookingNow, setBookingNow] = useState(false);
  const panelScrollRef = useRef<ScrollView>(null);
  const handleItemTypeChange = (itemType: CatalogProductFormFieldsProps['draft']['itemType']) => {
    const changedToService = props.draft.itemType !== 'service' && itemType === 'service';
    if (changedToService && serviceCatalogEnabled) {
      if (railMode) {
        setActiveSection('booking');
        panelScrollRef.current?.scrollTo({ y: 0, animated: false });
      }
      setBookingNow(true);
    } else if (props.draft.itemType === 'service' && itemType !== 'service') {
      setActiveSection('identity');
      setBookingNow(false);
      panelScrollRef.current?.scrollTo({ y: 0, animated: false });
    }
    props.onItemTypeChange(itemType);
  };

  // Alta en servicio (default o ya elegido): el rail arranca en Reserva y cobro.
  useEffect(() => {
    if (
      formMode === 'create' &&
      railMode &&
      props.draft.itemType === 'service' &&
      serviceCatalogEnabled
    ) {
      setActiveSection('booking');
      setBookingNow(true);
    }
  }, [formMode, railMode, props.draft.itemType, serviceCatalogEnabled]);

  // Si el Guardar falla, saltar al panel que contiene el campo con error —
  // en rail el error está oculto si el usuario quedó en otra sección.
  useEffect(() => {
    if (props.priceValidationMessage) setActiveSection('pricing');
    else if (props.barcodeValidationMessage || props.codeValidationMessage) setActiveSection('codes');
    else if (props.draftValidationMessage) setActiveSection('identity');
  }, [
    props.priceValidationMessage,
    props.barcodeValidationMessage,
    props.codeValidationMessage,
    props.draftValidationMessage,
  ]);

  // Carga rápida (D): barcode primero, seguido por los datos básicos y el precio.
  // «Más opciones» conserva el draft y abre el formulario completo.
  if (showCompact) {
    return (
      <>
        <CatalogProductFormCodesFields
          {...props}
          barcodeInputRef={barcodeInputRef}
          desktopQuickMode={desktopQuickMode}
          mobile={mobile}
          compact
        />
        <CatalogProductFormIdentityFields
          {...props}
          desktopQuickMode={desktopQuickMode}
          mobile={mobile}
          onItemTypeChange={handleItemTypeChange}
          compact
        />
        <CatalogProductFormPricingFields
          {...props}
          desktopQuickMode={desktopQuickMode}
          mobile={mobile}
          compact
        />
        {mobile && onExpandForm ? (
          <View style={styles.ticketFormSection}>
            <TouchableOpacity
              onPress={onExpandForm}
              accessibilityRole="button"
              accessibilityLabel="Ver más opciones del producto"
              testID="catalog.form.expand"
            >
              <Text style={styles.ticketFormLink}>Más opciones</Text>
            </TouchableOpacity>
          </View>
        ) : null}
        <CatalogProductFormDraftValidation {...props} mobile={mobile} />
      </>
    );
  }

  // Conditions for conditional sections — checked here so dividers/rail only
  // show sections that apply.
  const showKitchen =
    props.draft.itemType === 'physical' && !!props.kitchenTicketsEnabled;
  const showSupplier = purchasesEnabled;
  // Filtros carta: alérgenos + usage_tags. Gate = señales gastro (no if rubro).
  const showMenuFilters =
    props.draft.itemType === 'physical' && !!props.menuDietaryFiltersEnabled;
  // Farmacia: capability `product_pharmacy_fields` (default ON en pharmacy_basic;
  // el owner la prende/apaga en Funciones del negocio). Nada de fork por rubro.
  const showPharmacy =
    props.draft.itemType === 'physical' && !!props.pharmacyFieldsEnabled;
  const showVariants = !!props.variantsEnabled && props.draft.itemType === 'physical';
  const productCombosEnabled = useHasBusinessCapability('product_combos');
  const recipesEnabled = useHasBusinessCapability('recipes');
  const canViewMenuOptions = useModulePermission('MenuOptions', 'view');
  const canEditMenuOptions = useModulePermission('MenuOptions', 'edit');
  const canEditCatalog = useModulePermission('Catalog', 'edit');
  const showCompositionCreate =
    productCombosEnabled &&
    canViewMenuOptions &&
    !formCompact &&
    formMode === 'create' &&
    props.draft.itemType === 'physical';
  const showCompositionEdit =
    productCombosEnabled &&
    canViewMenuOptions &&
    formMode === 'edit' &&
    props.draft.itemType === 'physical' &&
    props.editingProduct != null &&
    props.editingProduct.isMisc !== true;
  const showComposition = showCompositionCreate || showCompositionEdit;
  const showRecipe =
    recipesEnabled &&
    formMode === 'edit' &&
    props.draft.itemType === 'physical' &&
    props.editingProduct != null &&
    props.editingProduct.isMisc !== true &&
    props.hideStockPanels !== true;
  const recipeKind = useCatalogOverlayStore((s) => s.selectedProductRecipeKind);
  const setRecipeKind = useCatalogOverlayStore((s) => s.setSelectedProductRecipeKind);
  const pendingFormRailSection = useCatalogOverlayStore((s) => s.pendingFormRailSection);

  useEffect(() => {
    if (!pendingFormRailSection) return;
    const section = useCatalogOverlayStore.getState().consumeFormRailSectionRequest();
    if (!section) return;
    setActiveSection(section);
  }, [pendingFormRailSection, props.editingProduct?.id]);

  // Una sola lista de secciones para los dos layouts (rail desktop / stack
  // mobile). Orden: primero lo que define al producto, después lo operativo.
  const defs: FormSectionDef[] = [
    {
      key: 'identity',
      label: 'Qué vendés',
      glyph: '▣',
      node: (
        <CatalogProductFormIdentityFields
          {...props}
          desktopRail={railMode}
          mobile={mobile}
          onItemTypeChange={handleItemTypeChange}
        />
      ),
    },
    {
      key: 'codes',
      label: 'Códigos',
      glyph: '▤',
      optionalLabel: 'Opcional',
      node: (
        <CatalogProductFormCodesFields
          {...props}
          barcodeInputRef={barcodeInputRef}
          desktopRail={railMode}
          mobile={mobile}
        />
      ),
    },
    {
      key: 'pricing',
      label: 'Precio y stock',
      glyph: '＄',
      node: <CatalogProductFormPricingFields {...props} desktopRail={railMode} mobile={mobile} />,
    },
  ];
  if (showVariants) {
    defs.push({
      key: 'variants',
      label: 'Variantes',
      glyph: '⧉',
      optionalLabel: 'Opcional',
      node: <CatalogProductFormVariantsFields {...props} desktopRail={railMode} mobile={mobile} />,
    });
  }
  if (showComposition) {
    const compositionProductId = props.editingProduct?.id ?? '';
    const compositionName = props.draft.name.trim() || props.editingProduct?.name || 'Producto';
    defs.push({
      key: 'composition',
      label: 'Composición (venta)',
      glyph: '⊞',
      optionalLabel: showCompositionCreate ? 'Opcional' : undefined,
      node: (
        <CatalogProductFormCompositionFields
          mode={showCompositionEdit ? 'edit' : 'create'}
          organizationId={props.comboPickOrganizationId ?? null}
          productId={compositionProductId}
          productName={compositionName}
          canEdit={canEditMenuOptions}
          salePrice={Number(props.draft.price) || props.editingProduct?.price || 0}
          formatMoney={props.currencyFormatter.format}
          draftComponents={props.comboDraftComponents ?? []}
          onDraftChange={props.onComboDraftChange ?? (() => {})}
          onSaved={props.onComboCompositionSaved}
        />
      ),
    });
  }
  if (showRecipe && props.editingProduct) {
    defs.push({
      key: 'receta',
      label: 'Producción (receta)',
      glyph: '⚗',
      node: (
        <CatalogProductRecipePanel
          productId={props.editingProduct.id}
          productName={props.editingProduct.name}
          kind={recipeKind as RecipeKind}
          onKindChange={(k) => setRecipeKind(k)}
          showKindSwitch
          canEdit={canEditCatalog}
          onSaved={props.onRecipeSaved}
          onProduce={props.onRecipeProduce}
          onDisassembleRun={props.onRecipeDisassemble}
        />
      ),
    });
  }
  if (availabilityEnabled) {
    defs.push({
      key: 'public',
      label: 'Disponibilidad',
      glyph: '◎',
      node: (
        <CatalogProductFormPublicStoreFields
          {...props}
          onlineStoreEnabled={onlineStoreEnabled}
          menuQrEnabled={menuQrEnabled}
          posEnabled={posEnabled}
          desktopRail={railMode}
          mobile={mobile}
        />
      ),
    });
  }
  if (showBooking) {
    defs.push({
      key: 'booking',
      label: 'Reserva y cobro',
      glyph: '◷',
      optionalLabel: 'Servicios',
      node: (
        <CatalogProductFormBookingFields
          {...props}
          onlineStoreEnabled={onlineStoreEnabled}
          desktopRail={railMode}
          mobile={mobile}
        />
      ),
    });
  }
  if (showKitchen) {
    defs.push({
      key: 'kitchen',
      label: 'Destino producción',
      glyph: '♨',
      optionalLabel: 'Opcional',
      node: <CatalogProductFormKitchenDestinationFields {...props} desktopRail={railMode} mobile={mobile} />,
    });
  }
  if (showSupplier) {
    defs.push({
      key: 'org',
      label: 'Proveedor',
      glyph: '◇',
      optionalLabel: 'Opcional',
      node: <CatalogProductFormOrganizationFields {...props} desktopRail={railMode} mobile={mobile} />,
    });
  }
  if (showMenuFilters) {
    defs.push({
      key: 'menu-filters',
      label: 'Filtros de carta',
      glyph: '▦',
      optionalLabel: 'Opcional',
      node: <CatalogProductFormMenuFilterFields {...props} desktopRail={railMode} mobile={mobile} />,
    });
  }
  if (showPharmacy) {
    defs.push({
      key: 'pharmacy',
      label: 'Farmacia',
      glyph: '✚',
      optionalLabel: 'Opcional',
      node: <CatalogProductFormPharmacyFields {...props} desktopRail={railMode} mobile={mobile} />,
    });
  }
  defs.push({
    key: 'notes',
    label: 'Notas internas',
    glyph: '✎',
    optionalLabel: 'Opcional',
    node: <CatalogProductFormNotesFields {...props} desktopRail={railMode} />,
  });

  // Servicio: "Reserva y cobro" es central — sube junto a identidad.
  if (props.draft.itemType === 'service') {
    const bookingIdx = defs.findIndex((d) => d.key === 'booking');
    if (bookingIdx > 1) {
      const [booking] = defs.splice(bookingIdx, 1);
      defs.splice(1, 0, booking);
    }
  }

  // Si la sección activa desaparece (itemType/capabilities), caer a la primera.
  const activeKey = defs.some((d) => d.key === activeSection)
    ? activeSection
    : defs[0].key;

  // ● solo marca requeridos faltantes (nombre, precio) — no es un stepper.
  const missingRequired = (key: string) =>
    (key === 'identity' && !props.draft.name.trim()) ||
    (key === 'pricing' && !(props.draft.price ?? '').trim());

  if (railMode) {
    const activeIdx = defs.findIndex((d) => d.key === activeKey);
    const next = defs[activeIdx + 1];
    const selectSection = (key: string) => {
      setActiveSection(key);
      // Cada panel arranca desde arriba — no heredar el scroll del anterior.
      panelScrollRef.current?.scrollTo({ y: 0, animated: false });
    };
    return (
      <View style={styles.railBody}>
        <ScrollView
          style={styles.rail}
          contentContainerStyle={styles.railContent}
          testID="catalog.form.rail"
          showsVerticalScrollIndicator={false}
        >
          {defs.map((d) => (
            <TouchableOpacity
              key={d.key}
              style={[styles.railItem, activeKey === d.key && styles.railItemActive]}
              onPress={() => selectSection(d.key)}
              accessibilityRole="button"
              accessibilityLabel={`Sección ${d.label}`}
              testID={`catalog.form.section.${d.key}`}
            >
              <Text style={styles.railGlyph}>{d.glyph}</Text>
              <Text
                style={[styles.railLabel, activeKey === d.key && styles.railLabelActive]}
                numberOfLines={1}
              >
                {d.label}
              </Text>
              {bookingNow && d.key === 'booking' ? <Text style={styles.railNowBadge}>ahora aplica</Text> : null}
              {missingRequired(d.key) ? <Text style={styles.railPending}>●</Text> : null}
            </TouchableOpacity>
          ))}
          <Text style={styles.railFoot}>● = campo obligatorio pendiente.</Text>
        </ScrollView>
        <View style={styles.panelColumn}>
          <ModalAwareScrollView
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ref={panelScrollRef as any}
            style={styles.panelScroll}
            contentContainerStyle={styles.panelContent}
            testID="catalog.form.panel-scroll"
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator={false}
            bottomOffset={spacing.sm}
          >
            {/* Panels montados con display:none — nada de estado se pierde al
                cambiar de sección (los campos siguen controlados por el draft). */}
            {defs.map((d) => (
              <View
                key={d.key}
                style={{ display: activeKey === d.key ? 'flex' : 'none' }}
                testID={`catalog.form.panel.${d.key}`}
              >
                <View style={styles.panelHeading}>
                  <View style={styles.panelHeadingGlyph}>
                    <Text style={styles.panelHeadingGlyphText}>{d.glyph}</Text>
                  </View>
                  <Text style={styles.panelHeadingLabel}>{d.label}</Text>
                  {d.optionalLabel ? <Text style={styles.panelHeadingBadge}>{d.optionalLabel}</Text> : null}
                </View>
                {d.node}
              </View>
            ))}
            <View style={{ marginTop: 14 }}>
              <CatalogProductFormDraftValidation {...props} mobile={mobile} />
            </View>
          </ModalAwareScrollView>
          {next ? (
            <View style={styles.panelNextDock}>
              <TouchableOpacity
                style={styles.panelNext}
                onPress={() => selectSection(next.key)}
                accessibilityRole="button"
                accessibilityLabel={`Siguiente: ${next.label}`}
                testID="catalog.form.next"
              >
                <Text style={styles.panelNextText}>Siguiente →</Text>
              </TouchableOpacity>
            </View>
          ) : null}
        </View>
      </View>
    );
  }

  // Stack mobile/sheet: mismo orden, dividers entre secciones visibles.
  return (
    <>
      {defs.map((d, i) => (
        <React.Fragment key={d.key}>
          {i > 0 ? <View style={styles.sectionDivider} /> : null}
          {d.node}
        </React.Fragment>
      ))}
      <CatalogProductFormDraftValidation {...props} mobile={mobile} />
    </>
  );
}
